import { Loader2 } from "lucide-react";
import { useCallback, useSyncExternalStore } from "react";

import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import {
  Dialog as ShadcnDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

import { dialogManager } from "./dialog-manager";
import type { DialogInstance } from "./dialog-types";

const DIALOG_PLACEMENT_CLASS_NAMES = {
  center: "",
  top: "!top-[8vh] !translate-y-0",
} as const;

function DialogRenderer({ dialog }: { dialog: DialogInstance }) {
  const isSubmitting = dialog.status === "submitting";
  const hasHeader = Boolean(dialog.title || dialog.description);
  const CustomComponent = dialog.kind === "custom" ? dialog.component : null;
  const placement = dialog.placement ?? "center";
  const closeCustomDialog = useCallback(() => {
    void dialogManager.dismiss(dialog.id);
  }, [dialog.id]);
  const setCustomDialogBeforeClose = useCallback(
    (handler?: Parameters<typeof dialogManager.setBeforeClose>[1]) => {
      dialogManager.setBeforeClose(dialog.id, handler);
    },
    [dialog.id],
  );

  const handleConfirm = async () => {
    if (dialog.kind !== "confirm") {
      return;
    }

    if (!dialog.onConfirm) {
      dialogManager.resolve(dialog.id, true);
      return;
    }

    dialogManager.setSubmitting(dialog.id, true);

    try {
      await dialog.onConfirm();
      dialogManager.resolve(dialog.id, true);
    } catch {
      dialogManager.setSubmitting(dialog.id, false);
    }
  };

  return (
    <ShadcnDialog
      open={true}
      onOpenChange={(open) => {
        if (!open) {
          void dialogManager.dismiss(dialog.id);
        }
      }}
    >
      <DialogContent
        {...(dialog.description ? {} : { "aria-describedby": undefined })}
        className={cn(
          DIALOG_PLACEMENT_CLASS_NAMES[placement],
          dialog.className,
        )}
        onEscapeKeyDown={(event) => {
          if (!dialog.closeOnEsc || isSubmitting) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (!dialog.closeOnOutside || isSubmitting) {
            event.preventDefault();
          }
        }}
      >
        {hasHeader ? (
          <DialogHeader>
            {dialog.title ? <DialogTitle>{dialog.title}</DialogTitle> : null}
            {dialog.description ? (
              <DialogDescription>
                {dialog.description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
        ) : null}

        {dialog.kind === "alert" ? (
          <DialogFooter>
            <Button
              type="button"
              onClick={() => dialogManager.resolve(dialog.id)}
            >
              {dialog.confirmText}
            </Button>
          </DialogFooter>
        ) : null}

        {dialog.kind === "confirm" ? (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => void dialogManager.dismiss(dialog.id)}
              disabled={isSubmitting}
            >
              {dialog.cancelText}
            </Button>
            <Button
              type="button"
              variant={dialog.variant}
              onClick={() => void handleConfirm()}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="animate-spin" /> : null}
              {dialog.confirmText}
            </Button>
          </DialogFooter>
        ) : null}

        {dialog.kind === "custom" && CustomComponent ? (
          <CustomComponent
            {...dialog.props}
            close={closeCustomDialog}
            resolve={(value: unknown) => dialogManager.resolve(dialog.id, value)}
            reject={(reason?: unknown) => dialogManager.reject(dialog.id, reason)}
            setBeforeClose={setCustomDialogBeforeClose}
            setSubmitting={(value: boolean) =>
              dialogManager.setSubmitting(dialog.id, value)
            }
          />
        ) : null}
      </DialogContent>
    </ShadcnDialog>
  );
}

export function DialogProvider() {
  const dialogs = useSyncExternalStore(
    dialogManager.subscribe,
    dialogManager.getState,
    dialogManager.getState,
  );

  return (
    <>
      {dialogs.map((dialog) => (
        <DialogRenderer key={dialog.id} dialog={dialog} />
      ))}
    </>
  );
}
